<template>
  <div
    ref="dragButton"
    class="drag-button"
    @mousedown="startDrag"
    @touchstart="startDrag"
    @click="handleClick"
    :style="buttonStyle"
    tabindex="0"
    role="button"
    aria-label="AI助手"
    @keydown.enter="openAssistant"
    @keydown.space="openAssistant"
    @mouseenter="showAIOptions"
    @mouseleave="startHideAIOptions"
  >
    <img
      src="@/assets/ai-assistant-icon.png"
      alt="AI助手"
      class="assistant-icon"
      draggable="false"
    />
    
    <!-- AI选项面板 -->
    <div 
      v-if="showOptions" 
      class="ai-options-panel"
      @mouseenter="cancelHideAIOptions"
      @mouseleave="startHideAIOptions"
    >
      <div 
        v-for="ai in aiOptions" 
        :key="ai.name"
        class="ai-option"
        @click="handleAIOptionClick(ai.url)"
      >
        <span class="ai-name">{{ ai.name }}</span>
        <span class="ai-desc">{{ ai.desc }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DraggableAssistantButton',
  props: {
    assistantUrl: {
      type: String,
      default: 'https://chat.deepseek.com/'
    },
    buttonSize: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      isDragging: false,
      offsetX: 0,
      offsetY: 0,
      posX: 0,
      posY: 0,
      hasMoved: false,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      moveHandler: null,
      sessionPosX: null,
      sessionPosY: null,
      showOptions: false,
      hideOptionsTimeout: null,
      // 添加点击防重标志
      isOpening: false,
      lastClickTime: 0,
      aiOptions: [
        {
          name: 'DeepSeek',
          desc: '深度求索AI助手',
          url: 'https://chat.deepseek.com/'
        },
        {
          name: '智谱清言',
          desc: '智谱AI对话助手',
          url: 'https://chatglm.cn/'
        },
        {
          name: '文心一言',
          desc: '百度AI大模型',
          url: 'https://yiyan.baidu.com/'
        },
        {
          name: '通义千问',
          desc: '阿里云AI助手',
          url: 'https://qianwen.aliyun.com/'
        },
        {
          name: '讯飞星火',
          desc: '科大讯飞AI',
          url: 'https://xinghuo.xfyun.cn/'
        }
      ]
    }
  },
  computed: {
    buttonStyle() {
      return {
        left: `${this.posX}px`,
        top: `${this.posY}px`,
        width: `${this.buttonSize}px`,
        height: `${this.buttonSize}px`,
        transform: this.isDragging ? 'scale(1.1)' : 'none'
      }
    }
  },
  created() {
    this.moveHandler = this.throttle(this.onMouseMove, 16)
    window.addEventListener('resize', this.handleResize)
  },
  mounted() {
    document.addEventListener('mousemove', this.moveHandler)
    document.addEventListener('touchmove', this.moveHandler, { passive: false })
    document.addEventListener('mouseup', this.stopDrag)
    document.addEventListener('touchend', this.stopDrag)
    window.addEventListener('mouseleave', this.stopDrag)

    this.initPosition()
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.moveHandler)
    document.removeEventListener('touchmove', this.moveHandler)
    document.removeEventListener('mouseup', this.stopDrag)
    document.removeEventListener('touchend', this.stopDrag)
    window.removeEventListener('mouseleave', this.stopDrag)
    window.removeEventListener('resize', this.handleResize)
    this.clearHideTimeout()
  },
  methods: {
    initPosition() {
      try {
        const storedX = sessionStorage.getItem('assistantPosX')
        const storedY = sessionStorage.getItem('assistantPosY')

        if (storedX !== null && storedY !== null) {
          this.sessionPosX = parseInt(storedX)
          this.sessionPosY = parseInt(storedY)
          this.posX = this.sessionPosX
          this.posY = this.sessionPosY
        } else {
          this.setDefaultPosition()
        }
        this.enforceBoundaries()
      } catch (e) {
        console.warn('Position load error:', e)
        this.setDefaultPosition()
      }
    },

    setDefaultPosition() {
      // 移动端默认放左下角，避开右侧的新增按钮与打赏按钮
      if (this.windowWidth <= 768) {
        this.posX = 16
        this.posY = this.windowHeight - this.buttonSize - 80
      } else {
        this.posX = this.windowWidth - this.buttonSize - 20
        this.posY = this.windowHeight - this.buttonSize - 20
      }
      this.sessionPosX = this.posX
      this.sessionPosY = this.posY
    },

    enforceBoundaries() {
      this.posX = Math.max(0, Math.min(this.posX, this.windowWidth - this.buttonSize))
      this.posY = Math.max(0, Math.min(this.posY, this.windowHeight - this.buttonSize))
    },

    startDrag(e) {
      // 如果点击的是选项面板，不启动拖动
      if (e.target.closest('.ai-options-panel')) {
        return
      }

      this.isDragging = true
      this.hasMoved = false
      this.clickStartTime = Date.now()
      this.hideAIOptions()

      const clientX = e.clientX !== undefined ? e.clientX : e.touches[0].clientX
      const clientY = e.clientY !== undefined ? e.clientY : e.touches[0].clientY

      const rect = this.$refs.dragButton.getBoundingClientRect()
      this.offsetX = clientX - rect.left
      this.offsetY = clientY - rect.top

      document.body.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'

      if (e.cancelable) e.preventDefault()
    },

    onMouseMove(e) {
      if (!this.isDragging) return

      if (!this.hasMoved) {
        const movedX = Math.abs(e.clientX - (this.posX + this.offsetX))
        const movedY = Math.abs(e.clientY - (this.posY + this.offsetY))
        this.hasMoved = movedX > 5 || movedY > 5
      }

      const clientX = e.clientX !== undefined ? e.clientX : e.touches[0].clientX
      const clientY = e.clientY !== undefined ? e.clientY : e.touches[0].clientY

      this.posX = clientX - this.offsetX
      this.posY = clientY - this.offsetY

      this.enforceBoundaries()

      if (e.cancelable) e.preventDefault()
    },

    stopDrag() {
      if (this.isDragging) {
        this.isDragging = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        this.savePosition()
        this.hasMoved = false
      }
    },

    savePosition() {
      try {
        sessionStorage.setItem('assistantPosX', this.posX)
        sessionStorage.setItem('assistantPosY', this.posY)
        this.sessionPosX = this.posX
        this.sessionPosY = this.posY
      } catch (e) {
        console.warn('Position save error:', e)
      }
    },

    handleClick(e) {
      // 如果点击的是选项面板，不处理主按钮点击
      if (e.target.closest('.ai-options-panel')) {
        return
      }

      if (e.button === 0) {
        const clickDuration = Date.now() - this.clickStartTime
        if (clickDuration < 300 && !this.hasMoved) {
          this.openAssistant()
        }
      }
    },

    openAssistant() {
      window.open(
        this.assistantUrl,
        '_blank',
        'noopener,noreferrer'
      )
    },

    // 显示AI选项面板
    showAIOptions() {
      this.clearHideTimeout()
      this.showOptions = true
    },

    // 开始延迟隐藏AI选项面板
    startHideAIOptions() {
      this.hideOptionsTimeout = setTimeout(() => {
        this.showOptions = false
      }, 300)
    },

    // 取消隐藏AI选项面板
    cancelHideAIOptions() {
      this.clearHideTimeout()
    },

    // 立即隐藏AI选项面板
    hideAIOptions() {
      this.clearHideTimeout()
      this.showOptions = false
    },

    // 清除隐藏超时
    clearHideTimeout() {
      if (this.hideOptionsTimeout) {
        clearTimeout(this.hideOptionsTimeout)
        this.hideOptionsTimeout = null
      }
    },

    // 处理AI选项点击 - 完全重构
    handleAIOptionClick(url) {
      // 立即阻止所有事件冒泡
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      
      // 防止重复点击
      const now = Date.now()
      if (this.isOpening || (now - this.lastClickTime < 2000)) {
        console.log('点击被阻止，防重保护生效')
        return false
      }
      
      this.isOpening = true
      this.lastClickTime = now
      console.log('打开AI助手:', url)
      
      // 立即隐藏面板
      this.hideAIOptions()
      
      // 使用更长的延迟确保只执行一次
      setTimeout(() => {
        console.log('执行打开操作')
        this.safeOpenURL(url)
        
        // 重置标志
        setTimeout(() => {
          this.isOpening = false
        }, 1000)
      }, 50)
      
      return false
    },

    // 安全的打开URL方法
    safeOpenURL(url) {
      try {
        // 方法1: 直接window.open
        const newWindow = window.open('', '_blank')
        if (newWindow) {
          newWindow.opener = null
          newWindow.location.href = url
        } else {
          // 方法2: 使用a标签
          this.fallbackOpenURL(url)
        }
      } catch (error) {
        console.error('打开链接失败:', error)
        // 方法3: 最后的手段
        window.location.href = url
      }
    },

    // 备用的打开URL方法
    fallbackOpenURL(url) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      setTimeout(() => {
        document.body.removeChild(link)
      }, 100)
    },

    handleResize() {
      this.windowWidth = window.innerWidth
      this.windowHeight = window.innerHeight

      if (this.posX > this.windowWidth - this.buttonSize) {
        this.posX = this.windowWidth - this.buttonSize - 20
      }
      if (this.posY > this.windowHeight - this.buttonSize) {
        this.posY = this.windowHeight - this.buttonSize - 20
      }

      if (this.sessionPosX !== null && this.sessionPosY !== null) {
        this.savePosition()
      }
    },

    throttle(fn, wait) {
      let lastTime = 0
      return function () {
        const now = Date.now()
        if (now - lastTime >= wait) {
          fn.apply(this, arguments)
          lastTime = now
        }
      }
    }
  }
}
</script>

<style scoped>
.drag-button {
  position: fixed;
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: grab;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  user-select: none;
  z-index: 9999;
  display: inline-block;
  border-radius: 50%;
  touch-action: none;
  will-change: transform;
  transition: transform 0.2s ease;
}

.assistant-icon {
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  user-select: none;
  transition: transform 0.2s ease;
}

.drag-button:hover .assistant-icon {
  transform: scale(1.1);
}

.drag-button:active {
  cursor: grabbing;
  transform: scale(1.1);
}

.drag-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(58, 143, 243, 0.5);
}

/* AI选项面板样式 */
.ai-options-panel {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 10000;
  overflow: hidden;
  animation: fadeIn 0.2s ease;
}

/* 添加小三角形指向按钮 */
.ai-options-panel::after {
  content: '';
  position: absolute;
  bottom: -6px;
  right: 20px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid white;
}

.ai-option {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.ai-option:last-child {
  border-bottom: none;
}

.ai-option:hover {
  background-color: #f5f5f5;
}

.ai-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 2px;
}

.ai-desc {
  font-size: 12px;
  color: #666;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (hover: none) {
  .drag-button:hover .assistant-icon {
    transform: none;
  }
  
  .ai-options-panel {
    display: none;
  }
  
  /* 移动端点击显示 */
  .drag-button:active .ai-options-panel {
    display: block;
  }
}
</style>